import dayjs from "dayjs";
import { BaseAction } from "../../../core/actions/BaseAction";
import { calculatePrice } from "../../../Lib/Utils/validationUtil";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { INextAndPreviousTrip, IOffer } from "../../../Orchestrator/Types/Index";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { IGetPreviousAndNextTrainActionProps } from "../../../Orchestrator/Types/Interface/ActionParams/SearchBookingParams";
import { PRODUCT_TIER, ProductCodeEnum, ProductTier, TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { admissionProduct } from "../../../Lib/Utils/exchangeProductTags";

// The admission product's code — the ticket, not an ancillary listed before it.
const offerCode = (offer: IOffer): string | undefined => admissionProduct(offer)?.code;

/**
 * Quick exchange moves the passenger to another train on the SAME product and
 * class — exactly the booked product code, nothing else. The old tier fall-up
 * (Value↔Flex siblings, then Saver→Value→Premium) silently swapped the product
 * and charged the fare difference for an upgrade the user never asked for,
 * breaking the same-product exchange rule. Group-seat products (duo/quad) and
 * codes outside PRODUCT_TIER stay out of quick exchange, as before.
 */
function isQuickExchangeCode(code: string): boolean {
    const tier = PRODUCT_TIER[code as ProductCodeEnum];
    return tier !== undefined && tier !== ProductTier.GROUP_SEAT;
}

function selectExchangeOffersByTicketClass(offers: IOffer[], nextBookingTicketTypeCode: string): IOffer[] {
    if (!isQuickExchangeCode(nextBookingTicketTypeCode)) return [];
    return offers.filter((offer) => offerCode(offer) === nextBookingTicketTypeCode);
}

// Chip shows what the user actually pays at settlement. Mirrors the QE
// branch in PrepareBookingSummaryAction:
//
//   chip = max(0, exchangePrice − originalPrice) + exchangeFee
//
// `exchangePrice` is summed without the fee so the price-diff clamp doesn't
// silently consume the fee when the new fare is cheaper. The fee is then added
// back on top — always owed, regardless of how the price-diff lands.
// Single-code selection above guarantees one offer per passenger per trip,
// so per-passenger fees aren't double-counted.
function sumExchangePrice(offers: IOffer[]): number {
    return offers.reduce((sum, o) => {
        const exchangePrice = o.exchangePrice ? calculatePrice(o.exchangePrice) : 0;
        return sum + exchangePrice;
    }, 0);
}

function sumExchangeFees(offers: IOffer[]): number {
    return offers.reduce((sum, o) => {
        const exchangeFee = o.exchangeFee ? calculatePrice(o.exchangeFee) : 0;
        return sum + exchangeFee;
    }, 0);
}

function applyExchangeMetadataToTrip(
    trip: INextAndPreviousTrip,
    selectedOffers: IOffer[],
    nextBookingPrice: number
): void {
    const priced = selectedOffers.filter((o) => o.exchangePrice);
    if (priced.length === 0) return;

    const newPriceTotal = sumExchangePrice(priced);
    const feeTotal = sumExchangeFees(priced);
    const priceDiff = newPriceTotal - nextBookingPrice;
    trip.exchangePrice = Math.max(0, priceDiff) + feeTotal;

    const ids = priced.map((o) => o.offerId);
    trip.exchangeOfferId = ids.length === 1 ? ids[0] : ids;
    trip.exchangeOfferPassengerRefs = priced.map((o) => ({
        offerId: o.offerId,
        passengerRefs: o.passengerRefs || [],
    }));
}

class GetPreviousAndNextTrainAction extends BaseAction<IDefaultActionResult, IGetPreviousAndNextTrainActionProps> {

    override async run(): Promise<IDefaultActionResult> {

        let actionResult: IDefaultActionResult = {
            status: "fail",
            data: null,
            message: ""
        }

        try {
            const { allBookedOffer, nextBookingTicketTypeCode, nextTicketStartDate, trains, nextBooking, inboundTrip, outboundTrip, nextTicketTrainDirection } = this.params!            

            let previousOfferAndTrip: {
                offers: IOffer[];
                trip: INextAndPreviousTrip | null;
            } = {
                offers: [],
                trip: null
            }
            let nextOfferAndTrip: {
                offers: IOffer[];
                trip: INextAndPreviousTrip | null;
            } = {
                offers: [],
                trip: null
            }

            const nextTicketStartDateTime = new Date(nextTicketStartDate);
            // Walk outward from the booked train to the nearest train that sells
            // the booked product: the adjacent train may sell only other products,
            // and stopping there (as the old non-Premium branch did) hid the chip.
            // No product to match (mixed fares, no booked product, group seat or
            // unknown code): skip scanning the ~2 MB offer list per candidate.
            const canMatch = isQuickExchangeCode(nextBookingTicketTypeCode);
            const nearestTrainWithOffers = (candidates: INextAndPreviousTrip[]) => {
                for (const trip of canMatch ? candidates : []) {
                    const tripOffers = allBookedOffer.filter((offer: IOffer) => trip.id === offer?.tripCoverage?.coveredTripId);
                    const offers = selectExchangeOffersByTicketClass(tripOffers, nextBookingTicketTypeCode);
                    if (offers.length > 0) return { trip, offers };
                }
                // None qualifies: keep the adjacent trip (offer-less) — result.data
                // still reports it; the atom drops offer-less chips below.
                return candidates.length > 0 ? { trip: candidates[0], offers: [] as IOffer[] } : null;
            };

            const previous = nearestTrainWithOffers(
                trains
                    .filter((trip: INextAndPreviousTrip) => new Date(trip.startTime) < nextTicketStartDateTime)
                    // Return leg: never offer a train that leaves before the outbound arrives
                    // (mirror of the inbound overlap guard on the next train below).
                    .filter((trip: INextAndPreviousTrip) => !outboundTrip?.endTime || !dayjs(trip.startTime).isBefore(dayjs(outboundTrip.endTime)))
                    .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
            );
            const next = nearestTrainWithOffers(
                trains
                    .filter((trip: INextAndPreviousTrip) => new Date(trip.startTime) > nextTicketStartDateTime)
                    // Round trip: the next train must start and arrive before the
                    // inbound leaves (System Allows Booking Outbound Trip That
                    // Overlaps with Inbound Trip).
                    .filter((trip: INextAndPreviousTrip) => !inboundTrip
                        || (!dayjs(trip.startTime).isAfter(dayjs(inboundTrip.startTime))
                            && !dayjs(trip.endTime).isAfter(dayjs(inboundTrip.startTime))))
                    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
            );
            if (previous) previousOfferAndTrip = previous;
            if (next) nextOfferAndTrip = next;
            if (previousOfferAndTrip.offers.length > 0 && previousOfferAndTrip.trip != null) {
                let previousBookingPrice = 0;
                if (nextTicketTrainDirection === TripTypeEnum.OUT_BOUND) {
                    previousBookingPrice = nextBooking?.outboundConfirmedPrice || 0;
                } else {
                    previousBookingPrice = nextBooking?.inboundConfirmedPrice || 0;
                }
                applyExchangeMetadataToTrip(previousOfferAndTrip.trip, previousOfferAndTrip.offers, previousBookingPrice);
            }
            if (nextOfferAndTrip.offers.length > 0 && nextOfferAndTrip.trip != null) {
                let nextBookingPrice = 0;
                if (nextTicketTrainDirection === TripTypeEnum.OUT_BOUND) {
                    nextBookingPrice = nextBooking?.outboundConfirmedPrice || 0;
                } else {
                    nextBookingPrice = nextBooking?.inboundConfirmedPrice || 0;
                }
                applyExchangeMetadataToTrip(nextOfferAndTrip.trip, nextOfferAndTrip.offers, nextBookingPrice);
            }

            updateRecoilState("nextAndPreviousTrainAtom", {
                previousTrain: previousOfferAndTrip.offers.length > 0 ? previousOfferAndTrip.trip : null,
                nextTrain: nextOfferAndTrip.offers.length > 0 ? nextOfferAndTrip.trip : null
            })
            actionResult.data = {
                previousTrain: previousOfferAndTrip.trip,
                nextTrain: nextOfferAndTrip.trip
            }
            actionResult.status = "success";

        } catch (error: any) {
            console.error("Error fetching stops:", error);
            actionResult.message = "Something went wrong in GetPreviousAndNextTrainAction";
        } finally {
            return actionResult;
        }
    }
}

export const getPreviousAndNextTrainAction = new GetPreviousAndNextTrainAction()

